"use client"

import { useMemo } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  DEFAULT_SERVICES,
  type ServiceCardData,
} from "@/components/ui/services-carousel"

/** Translation key map for each service card, keyed by service path */
const SERVICE_KEYS: Record<
  string,
  {
    title: string
    desc: string
    bioLabel?: string
    bioValue?: string
    bioStatus?: string
    bioUnit?: string
    bioRange?: string
  }
> = {
  "/tests/full-body-mri": {
    title: "serviceCardMriTitle",
    desc: "serviceCardMriDesc",
    bioLabel: "serviceMriBioLabel",
    bioValue: "serviceMriBioValue",
    bioStatus: "serviceMriBioStatus",
  },
  "/tests/advanced-blood-test": {
    title: "serviceCardBloodTitle",
    desc: "serviceCardBloodDesc",
    bioLabel: "serviceBloodBioLabel",
    bioValue: "serviceBloodBioValue",
    bioStatus: "serviceBloodBioStatus",
  },
  "/tests/body-composition": {
    title: "serviceCardBodyCompTitle",
    desc: "serviceCardBodyCompDesc",
    bioLabel: "serviceBodyCompBioLabel",
    bioValue: "serviceBodyCompBioValue",
    bioStatus: "serviceBodyCompBioStatus",
    bioUnit: "serviceBodyCompBioUnit",
    bioRange: "serviceBodyCompBioRange",
  },
  "/tests/dexa-scan": {
    title: "serviceCardDexaTitle",
    desc: "serviceCardDexaDesc",
    bioLabel: "serviceDexaBioLabel",
    bioValue: "serviceDexaBioValue",
    bioStatus: "serviceDexaBioStatus",
  },
  "/tests/gender-health": {
    title: "serviceCardGenderHealthTitle",
    desc: "serviceCardGenderHealthDesc",
    bioLabel: "serviceGenderHealthBioLabel",
    bioValue: "serviceGenderHealthBioValue",
    bioStatus: "serviceGenderHealthBioStatus",
  },
  "/tests/brain-volumetry": {
    title: "serviceCardBrainTitle",
    desc: "serviceCardBrainDesc",
    bioLabel: "serviceBrainBioLabel",
    bioValue: "serviceBrainBioValue",
    bioStatus: "serviceBrainBioStatus",
    bioUnit: "serviceBrainBioUnit",
    bioRange: "serviceBrainBioRange",
  },
  "/tests/omega-blood-test": {
    title: "serviceCardOmegaTitle",
    desc: "serviceCardOmegaDesc",
    bioLabel: "serviceOmegaBioLabel",
    bioValue: "serviceOmegaBioValue",
    bioStatus: "serviceOmegaBioStatus",
    bioUnit: "serviceOmegaBioUnit",
    bioRange: "serviceOmegaBioRange",
  },
  "/tests/mamma-mri": {
    title: "serviceCardMammaTitle",
    desc: "serviceCardMammaDesc",
    bioLabel: "serviceMammaBioLabel",
    bioValue: "serviceMammaBioValue",
    bioStatus: "serviceMammaBioStatus",
  },
  "/tests/prostate-mri": {
    title: "serviceCardProstateTitle",
    desc: "serviceCardProstateDesc",
    bioLabel: "serviceProstateBioLabel",
    bioValue: "serviceProstateBioValue",
    bioStatus: "serviceProstateBioStatus",
  },
  "/tests/pelvic-mri": {
    title: "serviceCardPelvicTitle",
    desc: "serviceCardPelvicDesc",
    bioLabel: "servicePelvicBioLabel",
    bioValue: "servicePelvicBioValue",
    bioStatus: "servicePelvicBioStatus",
  },
  "/tests/hormone-blood-test-men": {
    title: "serviceCardHormoneMenTitle",
    desc: "serviceCardHormoneMenDesc",
    bioLabel: "serviceHormoneMenBioLabel",
    bioValue: "serviceHormoneMenBioValue",
    bioStatus: "serviceHormoneMenBioStatus",
  },
  "/tests/hormone-blood-test-women": {
    title: "serviceCardHormoneWomenTitle",
    desc: "serviceCardHormoneWomenDesc",
    bioLabel: "serviceHormoneWomenBioLabel",
    bioValue: "serviceHormoneWomenBioValue",
    bioStatus: "serviceHormoneWomenBioStatus",
    bioUnit: "serviceHormoneWomenBioUnit",
    bioRange: "serviceHormoneWomenBioRange",
  },
  "/tests/calcium-score": {
    title: "serviceCardCalciumTitle",
    desc: "serviceCardCalciumDesc",
    bioLabel: "serviceCalciumBioLabel",
    bioValue: "serviceCalciumBioValue",
    bioStatus: "serviceCalciumBioStatus",
    bioUnit: "serviceCalciumBioUnit",
    bioRange: "serviceCalciumBioRange",
  },
  "/tests/essential-b-vitamins-test": {
    title: "serviceCardBVitaminsTitle",
    desc: "serviceCardBVitaminsDesc",
    bioLabel: "serviceBVitaminsBioLabel",
    bioValue: "serviceBVitaminsBioValue",
    bioStatus: "serviceBVitaminsBioStatus",
    bioUnit: "serviceBVitaminsBioUnit",
    bioRange: "serviceBVitaminsBioRange",
  },
  "/tests/lpa": {
    title: "serviceCardLpaTitle",
    desc: "serviceCardLpaDesc",
    bioLabel: "serviceLpaBioLabel",
    bioValue: "serviceLpaBioValue",
    bioStatus: "serviceLpaBioStatus",
  },
  "/tests/cortisol-blood-test": {
    title: "serviceCardCortisolTitle",
    desc: "serviceCardCortisolDesc",
    bioLabel: "serviceCortisolBioLabel",
    bioValue: "serviceCortisolBioValue",
    bioStatus: "serviceCortisolBioStatus",
  },
  "/tests/psa-blood-test": {
    title: "serviceCardPsaTitle",
    desc: "serviceCardPsaDesc",
    bioLabel: "servicePsaBioLabel",
    bioValue: "servicePsaBioValue",
    bioStatus: "servicePsaBioStatus",
  },
}

/** Returns DEFAULT_SERVICES with translated titles, descriptions, and biomarker overlays */
export function useTranslatedServices({
  showNewTestsPages = false,
}: { showNewTestsPages?: boolean } = {}): ServiceCardData[] {
  const ut = useTranslations("ui")
  return useMemo(
    () =>
      DEFAULT_SERVICES.filter(
        (service) =>
          showNewTestsPages || service.path !== "/tests/gender-health"
      ).map((service) => {
        const keys = SERVICE_KEYS[service.path]
        if (!keys) return service
        const bio = service.biomarker
        return {
          ...service,
          title: ut(keys.title as never),
          description: ut(keys.desc as never),
          ...(keys.bioLabel && bio
            ? {
                biomarker: {
                  ...bio,
                  label: ut(keys.bioLabel as never),
                  value: ut(keys.bioValue as never),
                  status: ut(keys.bioStatus as never),
                  ...(bio.type === "pill" && keys.bioUnit
                    ? {
                        unit: ut(keys.bioUnit as never),
                        range: ut(keys.bioRange as never),
                      }
                    : {}),
                },
              }
            : {}),
        }
      }),
    [ut, showNewTestsPages]
  )
}
